import React, { useState } from 'react';
import { X, CheckCheck, AlertTriangle } from 'lucide-react';
import C from '../../constants/colors';

export default function ResetAlertsModal({ companies, sites, onReset, onClose }) {
  const [companyId, setCompanyId] = useState('');
  const [scopeValue, setScopeValue] = useState('__all__');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { acknowledged: N }

  const filteredSites = companyId ? sites.filter(s => s.companyId === companyId) : sites;
  const selectedCompany = companies.find(c => c.id === companyId);

  function getScopePayload() {
    if (scopeValue === '__all__') {
      // Company selected → limit to that company; otherwise the whole tenant.
      return companyId ? { scope: 'company', companyId } : { scope: 'all' };
    }
    if (scopeValue === '__offsite__') return { scope: 'offsite' };
    return { scope: 'site', siteId: scopeValue };
  }

  // Human-readable description of exactly what will be reset.
  function scopeSummary() {
    if (scopeValue === '__all__') {
      return companyId
        ? `All alerts for ${selectedCompany?.name || 'this company'}`
        : 'ALL alerts across ALL companies';
    }
    if (scopeValue === '__offsite__') return 'Offsite alerts only';
    const site = sites.find(s => s.id === scopeValue);
    return `Site: ${site?.name || scopeValue}`;
  }
  const isTenantWide = scopeValue === '__all__' && !companyId;

  async function handleReset() {
    setLoading(true);
    try {
      const res = await onReset(getScopePayload());
      setResult(res);
      setTimeout(() => onClose(), 1500);
    } finally {
      setLoading(false);
    }
  }

  const selStyle = {
    width: '100%', background: C.surface, border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '10px 12px', color: C.textPrimary,
    fontSize: '13px', fontFamily: 'inherit', outline: 'none',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '32px', width: '460px', maxWidth: '95vw' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary, margin: 0 }}>Reset Alerts</h3>
            <p style={{ fontSize: '13px', color: C.textMuted, marginTop: '4px' }}>Bulk-acknowledge outstanding alerts by scope</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}><X size={20} /></button>
        </div>

        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Company</label>
          <select value={companyId} onChange={e => { setCompanyId(e.target.value); setScopeValue('__all__'); }} style={selStyle}>
            <option value=''>All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Scope</label>
          <select value={scopeValue} onChange={e => setScopeValue(e.target.value)} style={selStyle}>
            <option value='__all__'>{companyId ? `All of ${selectedCompany?.name || 'company'}` : 'All Sites & Offsite'}</option>
            {!companyId && <option value='__offsite__'>Offsite Only</option>}
            {filteredSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div style={{ background: (isTenantWide ? C.red : C.blue) + '18', border: `1px solid ${(isTenantWide ? C.red : C.blue)}44`, borderRadius: '8px', padding: '10px 12px', marginBottom: '12px' }}>
          <div style={{ fontSize: '11px', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: '4px' }}>Will reset</div>
          <div style={{ fontSize: '14px', color: isTenantWide ? C.red : C.textPrimary, fontWeight: 700 }}>{scopeSummary()}</div>
        </div>

        <div style={{ background: C.amber + '18', border: `1px solid ${C.amber}44`, borderRadius: '8px', padding: '10px 12px', marginBottom: '20px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <AlertTriangle size={14} color={C.amber} style={{ marginTop: '1px', flexShrink: 0 }} />
          <span style={{ fontSize: '12px', color: C.amber, lineHeight: '1.5' }}>
            Marks matching alerts as acknowledged and clears SAFE / NEED&nbsp;HELP status back to NOT&nbsp;RESPONDED for employees and visitors in scope. Alerts remain in the audit log and are never deleted.
          </span>
        </div>

        {result && (
          <div style={{ background: C.green + '18', border: `1px solid ${C.green}44`, borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', textAlign: 'center' }}>
            <span style={{ fontSize: '13px', color: C.green, fontWeight: 600 }}>
              {result.acknowledged === 0 ? 'No unacknowledged alerts found.' : `${result.acknowledged} alert${result.acknowledged !== 1 ? 's' : ''} acknowledged.`}
            </span>
          </div>
        )}

        <button onClick={handleReset} disabled={loading || !!result} style={{
          width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
          background: loading || result ? C.surface : C.amber,
          color: loading || result ? C.textMuted : '#000',
          fontSize: '15px', fontWeight: 700, cursor: loading || result ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}>
          <CheckCheck size={16} />
          {loading ? 'Resetting…' : result ? 'Done' : 'Reset Alerts'}
        </button>
      </div>
    </div>
  );
}
